import { useCallback, useEffect } from 'react';
import { Prompt } from '../../Types';
import {
    setPrompts,
    setBasePrompt,
    setVisionPrompt,
    setSelectedPrompt,
    setCombinedPrompts,
} from '../../Store/features/prompt/promptSlice';
import { useAppDispatch, useAppSelector } from '../../store';

const usePromptState = () => {
    const dispatch = useAppDispatch();
    const prompts = useAppSelector((state) => state.prompt.prompts);
    const basePrompt = useAppSelector((state) => state.prompt.basePrompt);
    const visionPrompt = useAppSelector((state) => state.prompt.visionPrompt);
    const selectedPrompt = useAppSelector((state) => state.prompt.selectedPrompt) || basePrompt;
    const combinedPrompts = useAppSelector((state) => state.prompt.combinedPrompts);

    useEffect(() => {
        const foundBasePrompt = prompts.find((prompt: Prompt) => prompt.isBasePrompt);
        if (foundBasePrompt) {
            dispatch(setBasePrompt(foundBasePrompt));

            if (selectedPrompt === undefined) {
                dispatch(setSelectedPrompt(foundBasePrompt));
            }
        }
    }, [prompts, selectedPrompt, dispatch]);

    const updatePrompt = useCallback(
        (identifier: string, value: Partial<Prompt>) => {
            const updatedPrompts = prompts.map((prompt) =>
                prompt.identifier === identifier ? { ...prompt, ...value } : prompt,
            );
            dispatch(setPrompts(updatedPrompts));
        },
        [dispatch, prompts],
    );

    const updatePrompts = useCallback(
        (newPrompts: Prompt[]) => {
            dispatch(setPrompts(newPrompts));
        },
        [dispatch],
    );

    const deletePrompt = useCallback(
        (identifier: string) => {
            const updatedPrompts = prompts.filter((prompt) => prompt.identifier !== identifier);
            dispatch(setPrompts(updatedPrompts));
        },
        [dispatch, prompts],
    );

    const getPromptValueById = useCallback(
        <K extends keyof Prompt>(identifier: string, key?: K): K extends keyof Prompt ? Prompt[K] : Prompt => {
            const prompt = prompts.find((prompt: Prompt) => prompt.identifier === identifier) ?? basePrompt!;

            if (key) {
                return prompt[key] as any;
            }

            return prompt as any;
        },
        [prompts, basePrompt],
    );

    const updateBasePrompt = useCallback(
        (prompt: Prompt) => {
            dispatch(setBasePrompt(prompt));
        },
        [dispatch],
    );

    const updateVisionPrompt = useCallback(
        (prompt: Prompt) => {
            dispatch(setVisionPrompt(prompt));
        },
        [dispatch],
    );

    const updateSelectedPrompt = useCallback(
        (prompt: Prompt) => {
            dispatch(setSelectedPrompt(prompt));
        },
        [dispatch],
    );

    const updateCombinedPrompts = useCallback(
        (prompts: Prompt[]) => {
            dispatch(setCombinedPrompts(prompts));
        },
        [dispatch],
    );

    return {
        prompts,
        basePrompt,
        visionPrompt,
        selectedPrompt,
        combinedPrompts,
        setPrompts: updatePrompts,
        setBasePrompt: updateBasePrompt,
        setVisionPrompt: updateVisionPrompt,
        setSelectedPrompt: updateSelectedPrompt,
        setCombinedPrompts: updateCombinedPrompts,
        getPromptValueById,
        updatePrompt,
        deletePrompt,
    };
};
export default usePromptState;
